import {
  CardioOrResistance, Gym, GymTypes, IVideoToCache,
} from 'fitbeat.models';
import {
  AreaCategory, getUniqueCircuitAndStaticAreas, hasExerciseForZoneScreen, ZoneMappings,
} from '../../config/zone-mappings';
import { Exercise, WorkoutSettings } from '../../entities';
import { WorkoutSelection } from '../../entities/workout/workoutselection';
import { IExerciseRepository } from '../../persistence';
import { CurrentWorkoutSelectionManager } from './currentWorkoutSelectionManager';
import { WarmupAndWarmdownSelector } from './exerciseselectors/warmupAndWarmdownSelector';
import { WorkoutSelectionManager } from './workoutselectionmanager';

export class WorkoutSelectionVideoCache {
  public static getZoneTypeNumbers(gym: Gym, zoneNumbers: number[]): number[] {
    return zoneNumbers.flatMap((zoneNumber: number) => ZoneMappings.getZoneTypesForNumberInGym(zoneNumber, gym));
  }

  private warmupWarmdownSelector: WarmupAndWarmdownSelector;

  constructor(
    private currentWorkoutSelectionManager: CurrentWorkoutSelectionManager,
    private exerciseRepository: IExerciseRepository,
    private logger: any,
  ) {
    this.warmupWarmdownSelector = new WarmupAndWarmdownSelector();
  }

  public async getVideosToCacheForZones(
    zoneNumbers: number[],
    gym: Gym,
    date: Date = new Date(),
  ): Promise<IVideoToCache[]> {
    const allVideosToCache: IVideoToCache[] = [];

    const zoneTypeNumbers = WorkoutSelectionVideoCache.getZoneTypeNumbers(gym, zoneNumbers);
    const uniqueZoneTypeNumbers = Array.from(new Set(zoneTypeNumbers));
    const uniqueZoneTypesWithVideos = uniqueZoneTypeNumbers.filter(hasExerciseForZoneScreen);
    const { circuitZoneTypeNumbers, staticFunctionalAreas } = getUniqueCircuitAndStaticAreas(uniqueZoneTypeNumbers);
    const { timezone } = gym;

    await this.addWorkoutSelectionFallbackVideosForCircuit(allVideosToCache, circuitZoneTypeNumbers);
    await this.addWorkoutSelectionFallbackVideosForStaticFunctional(allVideosToCache, staticFunctionalAreas);
    if (uniqueZoneTypesWithVideos.length > 0) {
      await this.addWarmupsAndWarmdowns(allVideosToCache, date);
    }
    await this.addAllCurrentWorkoutSelectionCircuitVideos(allVideosToCache, timezone, circuitZoneTypeNumbers);
    await this.addAllCurrentWorkoutSelectionStaticFunctionalVideos(allVideosToCache, timezone, staticFunctionalAreas);

    return allVideosToCache;
  }

  private async addAllCurrentWorkoutSelectionCircuitVideos(
    allVideosToCache: IVideoToCache[],
    gymTimezone: string,
    zoneTypeNumbers: number[],
  ) {
    const gymType = GymTypes.Indoor;
    const allCombinations = CurrentWorkoutSelectionManager.getAllSettingCombinations(gymType);

    for (const workoutSettingsCombination of allCombinations) {
      const workoutSelectionForForSettings = await this.currentWorkoutSelectionManager
        .getCurrentWorkoutSelectionOrFail(gymType, workoutSettingsCombination, gymTimezone);

      zoneTypeNumbers.forEach((zoneTypeNumber: number) => {
        this.mapWorkoutSelectionToVideos(workoutSelectionForForSettings, allVideosToCache, zoneTypeNumber);
      });
    }
  }

  private async addAllCurrentWorkoutSelectionStaticFunctionalVideos(
    allVideosToCache: IVideoToCache[],
    gymTimezone: string,
    staticFunctionalAreas: AreaCategory[],
  ) {
    const homeWorkoutsForStudioStaticFunctional = GymTypes.Home;
    const zoneTypeNumber = 'any-zone'; // the exercises are for home, so ignore the type

    for (const area of staticFunctionalAreas) {
      const allSettings = CurrentWorkoutSelectionManager.getAllStaticFunctionalZoneSettingCombinations(area);

      for (const workoutSettings of allSettings) {
        const workoutSelectionForForSettings = await this.currentWorkoutSelectionManager
          .getCurrentWorkoutSelectionOrFail(
            homeWorkoutsForStudioStaticFunctional,
            workoutSettings,
            gymTimezone,
          );

        this.mapWorkoutSelectionToVideos(workoutSelectionForForSettings, allVideosToCache, zoneTypeNumber);
      }
    }
  }

  private async getFallbackWorkoutSelections(area: AreaCategory): Promise<WorkoutSelection[]> {
    const cardioOrResistanceKeys = area === 'boxing'
      ? [CardioOrResistance.Cardio]
      : Object.keys(CardioOrResistance);
    const gymType = GymTypes.Indoor;
    const promises = cardioOrResistanceKeys.map(async (cardioOrResistance: string) => {
      const settings = new WorkoutSettings({ cardioOrResistance: CardioOrResistance[cardioOrResistance] });
      const fallbackReference = WorkoutSelectionManager.getDefaultWorkoutSelectionReference(gymType, area, settings);
      if (!fallbackReference) {
        throw new Error(`failed to load fallback reference for ${gymType} ${fallbackReference}`);
      }
      const workoutSelectionForForSettings = await this.currentWorkoutSelectionManager
        .getSelectionForReferenceNumber(fallbackReference);
      if (!workoutSelectionForForSettings) {
        throw new Error(`no workout selection, cant load videos for fallback: ${fallbackReference}`);
      }

      return workoutSelectionForForSettings;
    });

    return Promise.all(promises);
  }

  private async addWorkoutSelectionFallbackVideosForCircuit(
    allVideosToCache: IVideoToCache[],
    zoneTypeNumbers: number[],
  ) {
    const fallbackWorkoutSelections = await this.getFallbackWorkoutSelections('circuit');
    for (const workoutSelectionForForSettings of fallbackWorkoutSelections) {
      zoneTypeNumbers.forEach((zoneTypeNumber: number) => {
        this.mapWorkoutSelectionToVideos(workoutSelectionForForSettings, allVideosToCache, zoneTypeNumber);
      });
    }
  }

  private async addWorkoutSelectionFallbackVideosForStaticFunctional(
    allVideosToCache: IVideoToCache[],
    staticFunctionalAreas: AreaCategory[],
  ) {
    const promises = staticFunctionalAreas.map((area: AreaCategory) => this.getFallbackWorkoutSelections(area));
    const fallbacksPerArea: WorkoutSelection[][] = await Promise.all(promises);
    const fallbackWorkoutSelections: WorkoutSelection[] = fallbacksPerArea.flat();

    const zoneTypeNumber = 'any-zone'; // the exercises are for home, so ignore the type
    for (const workoutSelection of fallbackWorkoutSelections) {
      /**
       * only include the first and 2nd exercise selection in the videos to cache
       * zone fallback is currently only handling 4 exercises for static functional zones
       * so returning more than this will mean useless downloading of videos
       *
       * furthermore, these videos are _also_ set to be cached in the zoneServer's `getSignedFallbackVideos`
       * which loads the data from the _actual_ json file that holds the fallback data
       *
       * this function (and probably ...ForCircuit above) can then probably be removed? (though currently
       * tests depend on it)
       * or this can be expanded, and the fallback data be build from this returned data (instead of in the
       * zoneServer)
       */
      workoutSelection.exerciseSelections = workoutSelection.exerciseSelections.slice(0, 2);

      this.mapWorkoutSelectionToVideos(workoutSelection, allVideosToCache, zoneTypeNumber);
    }
  }

  private async addWarmupsAndWarmdowns(allVideosToCache: IVideoToCache[], date: Date) {
    const allExercises = await this.exerciseRepository.getAll();
    const uniqueWarmupWarmdownForDate = this.warmupWarmdownSelector
      .getUniqueWarmupWarmdownFiltersFor(date, allExercises);
    for (const exercise of uniqueWarmupWarmdownForDate) {
      if (!exercise) {
        continue;
      }
      this.addVideoFromExerciseIfNotAlreadyAdded(allVideosToCache, exercise);
    }
  }

  private buildVideoToCache({ _rev, uniqueReferenceNumber }: Exercise) {
    return {
      version: _rev,
      reference: uniqueReferenceNumber,
    };
  }

  private mapWorkoutSelectionToVideos(
    workoutSelection: WorkoutSelection,
    allVideosToCache: IVideoToCache[],
    zoneTypeNumber: number | 'any-zone',
  ) {
    try {
      for (const exerciseSelection of workoutSelection.exerciseSelections) {
        if (zoneTypeNumber !== 'any-zone' && exerciseSelection.zone !== zoneTypeNumber) {
          continue;
        }

        this.addVideoFromExerciseIfNotAlreadyAdded(allVideosToCache, exerciseSelection.exercise);
        this.addVideoFromExerciseIfNotAlreadyAdded(allVideosToCache, exerciseSelection.alternateExercise);
        this.addVideoFromExerciseIfNotAlreadyAdded(allVideosToCache, exerciseSelection.secondAlternateExercise);
        this.addVideoFromExerciseIfNotAlreadyAdded(allVideosToCache, exerciseSelection.thirdAlternateExercise);
      }
    } catch (e) {
      this.logger.error('failed to map workout selection to videos', e);
      throw e;
    }
  }

  private addVideoFromExerciseIfNotAlreadyAdded(allVideosToCache: IVideoToCache[], exercise: Exercise | undefined) {
    if (exercise && !this.videoAlreadyAdded(allVideosToCache, exercise.uniqueReferenceNumber)) {
      allVideosToCache.push(this.buildVideoToCache(exercise));
    }
  }

  private videoAlreadyAdded(videos: IVideoToCache[], reference: string) {
    return videos.some((video: IVideoToCache) => video.reference === reference);
  }
}
